/**
 * Everything the public page's FEEDBACK mode (`pages/s/[token].tsx`, `components/symphony/feedback-page/`) and its route
 * (`pages/api/feedback-page.ts`) decide, with no React and no runtime imports — a unit test must not import the app's
 * dependency graph (orion-web CLAUDE.md, "Tests").
 *
 * The page is a feedback round's link: someone a Symphony user asked ("what do you think of this?") opens it with no
 * account and no app, sees the thing, and answers. The round is OutreachService's (VMR `sharecard/FeedbackRounds`);
 * this side only renders, rate-limits and forwards. It lives at `/s/<token>`, beside the share card, because
 * symphony.wix.com's gateway lets a logged-out reader through `/s` and nothing like `/f`.
 *
 * WHO may write words (the service enforces it; this only shapes the page):
 * - a PERSONAL link (the round sent this person their own token) names the reader, so it gets the comment box;
 * - the OPEN link names nobody, so it takes a one-tap reaction only. Words go through an exit that says who wrote:
 *   "Sign in to comment" (Wix login, then the answer is that user's) or "Reply on WhatsApp" (a chat with Symphony's
 *   number, prefilled with the round's reference, so the reply is that phone's).
 *
 * - The token is a short token (12 base62 characters) or, for a link minted before short links, `<roundId>.<secret>`:
 *   the share card's shapes, so `/s/`'s token rule is reused as is.
 * - The answer is UNTRUSTED free text. The service is the authority (500 characters, the reaction allow-list, a
 *   per-link hourly limit); this copy only shapes what the page offers and what the route bothers to forward.
 */

export { isWellFormedToken, readRouteToken, isBotUserAgent } from './share-card-view';

/** The service's cap, mirrored so the box can count down. The service refuses anything longer. */
export const MAX_FEEDBACK_CHARS = 500;

/** The quick reactions the service accepts (FeedbackRounds.Reactions), in page order. */
export const FEEDBACK_REACTIONS = ['heart', 'fire', 'thumbs_up', 'thinking'] as const;
export type FeedbackReaction = (typeof FEEDBACK_REACTIONS)[number];

export const FEEDBACK_REACTION_GLYPH: Record<FeedbackReaction, string> = {
  heart: '❤️',
  fire: '🔥',
  thumbs_up: '👍',
  thinking: '🤔',
};

export function isFeedbackReaction(value: unknown): value is FeedbackReaction {
  return typeof value === 'string' && (FEEDBACK_REACTIONS as readonly string[]).includes(value);
}

/** What a link holder may see — exactly the service's PublicFeedbackRound, and nothing that names a party. */
export interface FeedbackView {
  senderName: string;
  /** Set for a personal link only: how the reader is addressed. The anonymous link has none. */
  recipientName?: string;
  assetTitle?: string;
  previewUrl: string;
  /**
   * The asset as a file to play (a video's mp4), when the round has one: the page plays it with `previewUrl` as its
   * poster. Only a Wix-hosted https address is kept (`playableMediaUrl`), because strangers' browsers load it.
   */
  mediaUrl?: string;
  /** What kind of thing it is, when the round knows: the ask names it ("designed this logo"). */
  assetKind?: FeedbackAssetKind;
  reactions: FeedbackReaction[];
  /** This device's own earlier answer, so a return visit shows it. */
  myReply?: { text: string; reaction?: FeedbackReaction };
  /**
   * The page's language for THIS reader, resolved by the service (the round owner's chat language; a personal invitee's
   * or a signed-in Symphony reader's own). A primary subtag (`he`), absent when the service sent none — the page then
   * renders English (`lib/page-locale.ts`). Never taken from anything the reader sends.
   */
  language?: string;
  /**
   * The owner's grammatical gender, when they said one (Recipient.grammatical_gender): Hebrew and Arabic then agree
   * with them ("עיצבה" for a woman). Absent, the page speaks in forms that need none. Never guessed from a name.
   */
  senderGender?: GrammaticalGender;
  /** The reader's, the same way, when the reader is a Symphony user who said: "לא בטוחה" for a woman. */
  readerGender?: GrammaticalGender;
  /**
   * The open link's WhatsApp reply reference (`[FR-<it>]`), a short code that names the round and opens nothing. Absent
   * for a personal link and for a round from before short links, whose reference is the round id in its long token.
   */
  replyReference?: string;
}

/** The service's PublicFeedbackRound, structurally (the generated client's type is assignable to it). */
export interface WirePublicFeedbackRound {
  senderDisplayName?: string;
  recipientDisplayName?: string;
  assetTitle?: string;
  previewUrl?: string;
  /** The asset as a playable file (PublicFeedbackRound.media_url); absent for a still. */
  mediaUrl?: string;
  /** FeedbackAsset.Type, by name or by number. */
  assetType?: string | number;
  reactions?: string[];
  myReply?: { text?: string; reaction?: string };
  /** The page's language for this reader, already resolved server-side; '' when unknown. */
  language?: string;
  /** The open link's WhatsApp reply reference; '' when it has none. */
  replyReference?: string;
  /** PublicFeedbackRound.sender_grammatical_gender: "feminine", "masculine" or ''. */
  senderGrammaticalGender?: string;
  /** PublicFeedbackRound.reader_grammatical_gender, the same. */
  readerGrammaticalGender?: string;
}

export type GrammaticalGender = 'feminine' | 'masculine';

/** "feminine" / "masculine" as the service sends it; anything else is no gender, and the page never guesses one. */
export function grammaticalGenderOf(value: unknown): GrammaticalGender | undefined {
  const g = text(value).toLowerCase();
  return g === 'feminine' || g === 'masculine' ? g : undefined;
}

/**
 * The keys whose words agree with the OWNER (he: "עיצב/עיצבה", ar: "صمّم/صمّمت"). Each has a `.masculine` and a
 * `.feminine` twin in every bundle; the plain key is worded so it needs no gender, for an owner who never said.
 * A language whose sentence does not change carries the same words in all three.
 */
export const SENDER_GENDERED_KEYS: ReadonlySet<string> = new Set([
  'feedbackPage.og.title',
  'feedbackPage.ask.wixel',
  ...['image', 'logo', 'video', 'presentation', 'document', 'icons', 'invitation', 'form'].map((k) => `feedbackPage.ask.wixel.${k}`),
  'feedbackPage.privacy',
  'feedbackPage.privacy.reaction',
  'feedbackPage.privacy.signedIn',
  'feedbackPage.sent',
]);

/** The keys whose words agree with the READER (he: "לא בטוח/בטוחה"), the same way. */
export const READER_GENDERED_KEYS: ReadonlySet<string> = new Set([
  'feedbackPage.reaction.thinking',
  'feedbackPage.answer.placeholder',
  'feedbackPage.more.label',
]);

/** `key`, or its twin for `gender` when the key is one that agrees with that person. */
export function genderedKey(key: string, gender: GrammaticalGender | undefined, keys: ReadonlySet<string>): string {
  return gender && keys.has(key) ? `${key}.${gender}` : key;
}

/** The kinds the ask can name, each with its own sentence in every locale (`feedbackPage.ask.*.<kind>`). */
export const FEEDBACK_ASSET_KINDS = ['image', 'logo', 'video', 'presentation', 'document', 'icons', 'invitation', 'form'] as const;
export type FeedbackAssetKind = (typeof FEEDBACK_ASSET_KINDS)[number];

/** FeedbackAsset.Type (Wixel's own names) → the ask's kind. OTHER, UNKNOWN_TYPE and any name not here say "this". */
const ASSET_KIND_BY_TYPE: Record<string, FeedbackAssetKind> = {
  IMAGE: 'image',
  LOGO: 'logo',
  VIDEO: 'video',
  SLIDE: 'presentation',
  DOC: 'document',
  PDF: 'document',
  ICONS: 'icons',
  EVENT: 'invitation',
  FORM: 'form',
};
/** FeedbackAsset.Type in proto order, for a round that comes back by number. */
const ASSET_TYPE_BY_NUMBER = ['UNKNOWN_TYPE', 'IMAGE', 'LOGO', 'VIDEO', 'SLIDE', 'DOC', 'PDF', 'ICONS', 'EVENT', 'FORM', 'OTHER'];

export function assetKindOf(type: unknown): FeedbackAssetKind | undefined {
  const name = typeof type === 'number' ? ASSET_TYPE_BY_NUMBER[type] : typeof type === 'string' ? type.trim().toUpperCase() : undefined;
  return name ? ASSET_KIND_BY_TYPE[name] : undefined;
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** The Wix media hosts a playable file may come from — the service's own rule (FeedbackRounds.PreviewHosts). */
const MEDIA_HOSTS = ['wixstatic.com', 'wixmp.com', 'wix.com', 'wixel.com', 'wixel.ai', 'parastorage.com'];

/** A playable file the page may hand a stranger's browser: https, on a Wix media host, no credentials. Else undefined. */
export function playableMediaUrl(value: unknown): string | undefined {
  const raw = text(value);
  if (!raw) return undefined;
  try {
    const url = new URL(raw);
    const host = url.hostname.toLowerCase();
    const onWix = MEDIA_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
    return url.protocol === 'https:' && !url.username && !url.password && onWix ? raw : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The wire round → the page's view, or null. A round with no https preview is no round to show: the page would render
 * a broken box to a stranger, and the service only ever stores a Wix-hosted https still.
 */
export function toFeedbackView(wire: WirePublicFeedbackRound | null | undefined): FeedbackView | null {
  const previewUrl = text(wire?.previewUrl);
  if (!wire || !/^https:\/\//i.test(previewUrl)) return null;
  const view: FeedbackView = {
    senderName: text(wire.senderDisplayName),
    previewUrl,
    reactions: (Array.isArray(wire.reactions) ? wire.reactions : []).filter(isFeedbackReaction),
  };
  if (view.reactions.length === 0) view.reactions = [...FEEDBACK_REACTIONS];
  const recipientName = text(wire.recipientDisplayName);
  if (recipientName) view.recipientName = recipientName;
  const assetTitle = text(wire.assetTitle);
  if (assetTitle) view.assetTitle = assetTitle;
  const assetKind = assetKindOf(wire.assetType);
  if (assetKind) view.assetKind = assetKind;
  const mediaUrl = playableMediaUrl(wire.mediaUrl);
  if (mediaUrl) view.mediaUrl = mediaUrl;
  const language = languageOf(wire.language);
  if (language) view.language = language;
  const senderGender = grammaticalGenderOf(wire.senderGrammaticalGender);
  if (senderGender) view.senderGender = senderGender;
  const readerGender = grammaticalGenderOf(wire.readerGrammaticalGender);
  if (readerGender) view.readerGender = readerGender;
  const replyReference = text(wire.replyReference);
  if (REPLY_REFERENCE.test(replyReference)) view.replyReference = replyReference;
  const replyText = typeof wire.myReply?.text === 'string' ? wire.myReply.text : '';
  const replyReaction = wire.myReply?.reaction;
  if (replyText || isFeedbackReaction(replyReaction)) {
    view.myReply = isFeedbackReaction(replyReaction) ? { text: replyText, reaction: replyReaction } : { text: replyText };
  }
  return view;
}

/** A language tag → its lower-case primary subtag (`he-IL` → `he`), or undefined for nothing that looks like one. */
export function languageOf(value: unknown): string | undefined {
  const base = text(value).split(/[-_]/)[0]?.toLowerCase() ?? '';
  return /^[a-z]{2,3}$/.test(base) ? base : undefined;
}

/**
 * The ask line's key and values. A round shared without the owner's name names nobody, so the ask is the plain
 * question rather than "Someone designed this…".
 */
export function askFor(view: Pick<FeedbackView, 'senderName' | 'assetKind'>): { key: string; values: Record<string, string> } {
  // A known kind has its own sentence ("designed this logo"); none, or OTHER, keeps "this".
  const kind = view.assetKind ? `.${view.assetKind}` : '';
  return view.senderName
    ? { key: `feedbackPage.ask.wixel${kind}`, values: { senderName: view.senderName } }
    : { key: `feedbackPage.ask.anonymous${kind}`, values: {} };
}

/** Characters as a reader counts them (code points), so an emoji is one, as the service counts it. */
/**
 * The earlier answer a returning reader's device left, to show as "Sent" instead of an empty form. The server render
 * cannot know it: the device key lives in this browser's storage, so the page asks once it has the key (the route's
 * `open`). Applied only while the reader has not started a new answer, so a late reply never overwrites what they
 * are doing.
 */
export function returningAnswer(
  fetched: FeedbackView | null | undefined,
  reader: { answered: boolean; status: string; reaction?: string; text: string },
): FeedbackView['myReply'] | undefined {
  const mine = fetched?.myReply;
  if (!mine || (!mine.text && !mine.reaction)) return undefined;
  if (reader.answered || reader.status !== 'idle' || reader.reaction || reader.text.trim()) return undefined;
  return mine;
}

export function feedbackLength(value: string): number {
  return Array.from(value).length;
}

export type FeedbackAction = 'open' | 'submit' | 'unsubscribe' | 'sign_in';

export type ParsedFeedbackRequest =
  | { ok: true; action: 'open'; token: string; deviceKey: string }
  | { ok: true; action: 'unsubscribe'; token: string }
  | { ok: true; action: 'sign_in'; token: string }
  | { ok: true; action: 'submit'; token: string; deviceKey: string; text: string; reaction?: FeedbackReaction }
  | { ok: false; error: 'invalid' | 'too_long' | 'empty' };

const DEVICE_KEY = /^[A-Za-z0-9_-]{8,64}$/;

/**
 * The route's body → what to forward, or why not. Nothing about who answers comes from here: the token says which
 * link, and the device key is only the page's own "replace my answer" handle (the service limits per LINK, never per
 * device, because a device key is the caller's claim).
 */
export function parseFeedbackRequest(body: unknown, isToken: (value: unknown) => boolean): ParsedFeedbackRequest {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const token = typeof b.token === 'string' ? b.token : '';
  if (!isToken(token)) return { ok: false, error: 'invalid' };
  if (b.action === 'unsubscribe') return { ok: true, action: 'unsubscribe', token };
  if (b.action === 'sign_in') return { ok: true, action: 'sign_in', token };
  const deviceKey = typeof b.deviceKey === 'string' && DEVICE_KEY.test(b.deviceKey) ? b.deviceKey : '';
  if (!deviceKey) return { ok: false, error: 'invalid' };
  if (b.action === 'open') return { ok: true, action: 'open', token, deviceKey };
  if (b.action !== 'submit') return { ok: false, error: 'invalid' };
  const raw = typeof b.text === 'string' ? b.text : '';
  const answer = raw.trim();
  if (feedbackLength(answer) > MAX_FEEDBACK_CHARS) return { ok: false, error: 'too_long' };
  if (b.reaction !== undefined && b.reaction !== null && b.reaction !== '' && !isFeedbackReaction(b.reaction)) return { ok: false, error: 'invalid' };
  const reaction = isFeedbackReaction(b.reaction) ? b.reaction : undefined;
  if (!answer && !reaction) return { ok: false, error: 'empty' };
  return reaction ? { ok: true, action: 'submit', token, deviceKey, text: answer, reaction } : { ok: true, action: 'submit', token, deviceKey, text: answer };
}

/**
 * The service's refusal, as the route answers it. A rate limit or a full round is told to the reader ("try again
 * later"); everything else — not found, expired, a bad token — is one generic failure, never an oracle.
 */
export function refusalOf(error: unknown): 'rate_limited' | 'sign_in' | 'failed' {
  const message = error instanceof Error ? error.message : String(error ?? '');
  if (/FEEDBACK_SIGN_IN_TO_COMMENT/.test(message)) return 'sign_in';
  return /FEEDBACK_RATE_LIMITED|FEEDBACK_ROUND_FULL/.test(message) ? 'rate_limited' : 'failed';
}

/** The request carries a Wix session cookie (`wixSession2`): the same presence check the sign-in bounce makes. */
export function hasWixSession(cookie: string | string[] | undefined): boolean {
  const value = Array.isArray(cookie) ? cookie.join('; ') : typeof cookie === 'string' ? cookie : '';
  return /(?:^|;\s*)wixSession2=/.test(value);
}

/** Which page the reader gets: their own link (the comment box) or the open link (one-tap reactions and two exits). */
export type FeedbackMode = 'personal' | 'open';

export function feedbackModeOf(view: Pick<FeedbackView, 'recipientName'>): FeedbackMode {
  return view.recipientName ? 'personal' : 'open';
}

/** Whether the page offers the comment box: a personal link, or a reader who is signed in. */
export function canCommentOn(mode: FeedbackMode, signedIn: boolean): boolean {
  return mode === 'personal' || signedIn;
}

const ROUND_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const REPLY_REFERENCE = /^[0-9A-Za-z]{12}$/;

/**
 * The round's reference for a WhatsApp reply, never the link, so a message that sits in a chat and in Meta's logs
 * carries no credential: `FR-<reply reference>` (the service's short code for the round's open link), else, for a round
 * from before short links, `FR-<round id>` (its long token's id half). Undefined when there is neither — a short token
 * IS the credential, so it is never the reference.
 */
export function roundReferenceOf(token: string, replyReference?: string): string | undefined {
  if (replyReference && REPLY_REFERENCE.test(replyReference)) return `FR-${replyReference}`;
  const id = (token ?? '').split('.')[0] ?? '';
  return (token ?? '').includes('.') && ROUND_ID.test(id) ? `FR-${id.toLowerCase()}` : undefined;
}

/**
 * `wa.me/<Symphony's number>?text=<prefill>`. THE PARSER CONTRACT (digest-engine, `feedback-round-whatsapp-entry.ts`):
 * the reference rides in square brackets (`[FR-<round id>]`) and the parser takes the LAST one in the message, so the
 * reader may type before or after it. test/feedback-page.test.ts pins that every catalog keeps `[{reference}]`.
 */
export function feedbackWhatsAppUrl(prefill: string, number: string): string {
  return `https://wa.me/${number}?text=${encodeURIComponent(prefill)}`;
}

/** What the page renders. Two states only — a round, or the one neutral screen. */
export type FeedbackPageProps =
  | { state: 'unavailable' }
  // `signedIn`: the request carried a Wix session, so the open link offers the comment box (the service decides whether
  // the session is real; a stale one answers `sign_in` and the page offers the exits again).
  | { state: 'ready'; token: string; feedback: FeedbackView; countOpen: boolean; signedIn: boolean };

/** The device key the page keeps, so a return visit shows (and edits) its own answer. Never throws. */
export const FEEDBACK_DEVICE_KEY_STORAGE = 'symphony-feedback-device';

export function readOrCreateFeedbackDeviceKey(storage?: Storage | null, random: () => string = randomKey): string {
  let store: Storage | null = null;
  try {
    store = storage === undefined ? (typeof window === 'undefined' ? null : window.localStorage) : storage;
  } catch {
    store = null;
  }
  try {
    const existing = store?.getItem(FEEDBACK_DEVICE_KEY_STORAGE);
    if (existing && DEVICE_KEY.test(existing)) return existing;
  } catch {
    /* unreadable: a fresh key for this page load */
  }
  const key = random();
  try {
    store?.setItem(FEEDBACK_DEVICE_KEY_STORAGE, key);
  } catch {
    /* cannot remember it: one answer per page load instead */
  }
  return key;
}

function randomKey(): string {
  const bytes = new Uint8Array(18);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const FEEDBACK_RETURN_PATH = '/s/return';

/**
 * Where sign-in sends the reader back: `/s/return?h=<sealed handle>` on this host (`lib/feedback-return-handle.ts`),
 * keeping only a preview fleet's `x-wix-route` (so a deploy preview returns to itself) and nothing else of the current
 * address — the token least of all.
 */
export function feedbackReturnUrl(origin: string, search: string, handle: string): string {
  const route = new URLSearchParams(search).get('x-wix-route');
  return `${origin}${FEEDBACK_RETURN_PATH}?h=${encodeURIComponent(handle)}${route ? `&x-wix-route=${encodeURIComponent(route)}` : ''}`;
}

/** `/s/return`'s redirect target: the round's page, with the preview route carried over the same way. */
export function feedbackReturnTarget(token: string, query: Record<string, string | string[] | undefined>): string {
  const raw = query['x-wix-route'];
  const route = Array.isArray(raw) ? raw[0] : raw;
  return `/s/${encodeURIComponent(token)}${route ? `?x-wix-route=${encodeURIComponent(route)}` : ''}`;
}
